// bellOnRoomin — Phase 6.7 サブ task 後追い (consult 2026-05-07).
//
// 旧版の `/bellon roomin` コマンド (= 入室お知らせベル切替) を新版に移植.
//
// ## 動作仕様 (consult 2026-05-07 で確定)
//
// - `/bellon roomin` でトグル. ON のときは `room.member-joined` 受信時に
//   Web Audio API で beep (sine 800Hz × 100ms 程度) を鳴らす.
// - `/bellon off` で明示 OFF.
// - 設定は `localStorage['oekaki:bellon-roomin']` に `'on' | 'off'` で永続化、
//   再ログイン後も維持する (= ユーザ期待値合わせ).
// - 効果音は **Web Audio API で oscillator** で生成する (asset ファイル不要).
// - autoplay restriction 対策: 初回 AudioContext 生成は **ユーザ操作**
//   (= `/bellon roomin` 入力時) なので問題なし.
// - AudioContext / oscillator が失敗したら **silent fail** (= UI には toast を
//   出さない、ベル機能は best-effort).
//
// ## ファイル構成 (= application/ 配下)
//
// - parse / state toggle / localStorage adapter は **このファイル** に集約.
// - audio play は別 adapter (`infrastructure/audio/...`) に置くべきだが、MVP
//   範囲では adapter インタフェースだけここに定義し、実装は ChatPanel 側で
//   inline に書く (= LoC 最小化、test 不要 = 手動確認).
//
// ## なぜ slashCommand.ts 本体に詰め込まなかったか
//
// slashCommand.ts は no-arg な 4 コマンド (`/help`, `/rom`, `/clear`,
// `/imagepaste`) を扱うシンプル設計. `/bellon` は **サブコマンド** (`roomin` /
// `off`) を取る初の例なので、そのために slashCommand.ts の parser スキーマを
// 拡張すると 4 コマンドの test がノイズで埋もれる. 代わりに専用 module に
// 切り出して slashCommand 側からは委譲だけする.
//
// (architecture.md §16, classic-ui-restore consult 2026-05-07)

const STORAGE_KEY = 'oekaki:bellon-roomin';

/**
 * 永続化するベル ON/OFF 状態の minimal storage interface.
 * `globalThis.localStorage` で satisfies; tests pass an in-memory mock.
 */
export interface BellOnStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type BellOnState = 'on' | 'off';

/** localStorage を試しに probe して返す. 失敗したら null. AnonUidStore と同流儀. */
const probeBrowserStorage = (): BellOnStorage | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = `${STORAGE_KEY}__probe__`;
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

/** localStorage アダプタ. storage が使えないときも silent に no-op で動く. */
export interface BellOnRoominStore {
  /** 現在の ON/OFF 状態を読む. 不在 / 不正値 / storage 不可なら 'off'. */
  read(): BellOnState;
  /** 状態を書き込む. storage 不可なら silent no-op. */
  write(state: BellOnState): void;
}

export interface CreateBellOnRoominStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * return a no-op store if unavailable.
   *
   * Pass `null` to **force** the no-storage path (= read always returns 'off',
   * write is no-op). Tests use this to verify graceful degradation.
   */
  storage?: BellOnStorage | null;
}

export const createBellOnRoominStore = (
  opts: CreateBellOnRoominStoreOptions = {},
): BellOnRoominStore => {
  const explicitStorage = opts.storage;
  const storage = explicitStorage === null ? null : (explicitStorage ?? probeBrowserStorage());

  return {
    read() {
      if (storage === null) return 'off';
      try {
        const v = storage.getItem(STORAGE_KEY);
        if (v === 'on' || v === 'off') return v;
        return 'off';
      } catch {
        return 'off';
      }
    },
    write(next) {
      if (storage === null) return;
      try {
        storage.setItem(STORAGE_KEY, next);
      } catch {
        // ignore quota / cookie-blocked
      }
    },
  };
};

// ─── /bellon サブコマンドの parser ─────────────────────────────────────────

/**
 * `/bellon ...` の args 部分を解釈する純粋関数.
 *
 * - `args === 'roomin'`: トグル (= ON ⇄ OFF を反転).
 *   旧版の cmdline 文化を踏襲して「同じ語で再実行 = 取り消し」のシンプルさ.
 * - `args === 'off'`: 明示 OFF.
 * - それ以外 (空 / 未知サブコマンド): 'unknown'.
 *
 * `'roomin'` が **トグル** なのは consult 2026-05-07 確定方針. 「再度実行で
 * OFF」と「`/bellon off` で OFF」の 2 経路を残すことで、IRC 系の慣例とユーザ
 * 直感の両方をカバーする.
 */
export type BellonSubCommand =
  | { readonly kind: 'toggle-roomin' }
  | { readonly kind: 'force-off' }
  | { readonly kind: 'unknown'; readonly raw: string };

export const parseBellonArgs = (args: string): BellonSubCommand => {
  const normalized = args.trim().toLowerCase();
  if (normalized === 'roomin') return { kind: 'toggle-roomin' };
  if (normalized === 'off') return { kind: 'force-off' };
  return { kind: 'unknown', raw: args };
};

/**
 * 現在の state とサブコマンドから「次の state」と「ユーザに見せるメッセージ」
 * を返す純粋関数.
 *
 * - `'toggle-roomin'`: cur が 'off' → 'on' (= 入室ベルを鳴らす旨を通知).
 *                     cur が 'on' → 'off' (= 取り消した旨を通知).
 * - `'force-off'`: 'off' に倒す. 既に 'off' でも冪等にメッセージは出す.
 * - `'unknown'`: 状態は変えず、ヘルプメッセージのみ.
 */
export interface BellonDispatchResult {
  readonly nextState: BellOnState;
  /** caller (slashCommand.ports) が `pushSystemLine` で chat に出すべき文言. */
  readonly systemLine: string;
}

export const dispatchBellonCommand = (
  cur: BellOnState,
  sub: BellonSubCommand,
): BellonDispatchResult => {
  switch (sub.kind) {
    case 'toggle-roomin': {
      if (cur === 'off') {
        return {
          nextState: 'on',
          systemLine: '入室お知らせベル: ON (誰かが入室するたびにベルが鳴ります)',
        };
      }
      return { nextState: 'off', systemLine: '入室お知らせベル: OFF' };
    }
    case 'force-off':
      return { nextState: 'off', systemLine: '入室お知らせベル: OFF' };
    case 'unknown':
      return {
        nextState: cur,
        systemLine: `/bellon の使い方: /bellon roomin (トグル) / /bellon off (= 明示 OFF)`,
      };
  }
};

// ─── 効果音アダプタ (= 手動確認、test 不要) ─────────────────────────────────

/**
 * ベルの効果音を鳴らすアダプタ. 失敗時は silent (= MVP では best-effort).
 *
 * 仕様 (consult 2026-05-07):
 * - sine wave 800Hz × 100ms.
 * - AudioContext は **再利用** (= 毎回 new するとブラウザの context limit に
 *   当たる. 1 タブ 1 個で十分).
 * - 初回呼び出しはユーザ操作経由 (= `/bellon roomin` 入力時) なので autoplay
 *   restriction には引っかからない.
 *
 * adapter は外部 (ChatPanel) から factory で生成された `playBell()` を受け取る
 * 設計. factory はラップする AudioContext を closure に閉じ込める.
 */
export interface BellSoundAdapter {
  /** ベルを 1 回鳴らす. 失敗しても throw しない. */
  play(): void;
}

/**
 * ブラウザ標準の AudioContext を使う `BellSoundAdapter` の factory.
 *
 * AudioContext / oscillator API が無い環境 (= jsdom / 古いブラウザ) では
 * `play()` が no-op になる.
 *
 * **このモジュールは pure logic + adapter の adapter 側なので vitest test
 * 対象外** (= jsdom で AudioContext を mock するのが面倒、手動確認に倒す).
 */
export const createBrowserBellSound = (): BellSoundAdapter => {
  type AudioCtxCtor = new () => AudioContext;
  const Ctor: AudioCtxCtor | null =
    typeof globalThis.AudioContext !== 'undefined'
      ? globalThis.AudioContext
      : // Safari < 14 互換 (将来削除可、念のため).
        ((globalThis as unknown as { webkitAudioContext?: AudioCtxCtor }).webkitAudioContext ??
        null);
  if (Ctor === null) {
    return { play: () => undefined };
  }
  let ctx: AudioContext | null = null;
  return {
    play() {
      try {
        if (ctx === null) ctx = new Ctor();
        // user-gesture 後は resume() が成功する. 失敗時は catch で握りつぶす.
        if (ctx.state === 'suspended') {
          void ctx.resume();
        }
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 800;
        // Click 軽減のため 5ms ramp で立ち上げ・立ち下げる (= ぷちっ音防止).
        const now = ctx.currentTime;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.2, now + 0.005);
        gain.gain.linearRampToValueAtTime(0, now + 0.1);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.1);
      } catch {
        // silent fail — MVP 範囲ではユーザに通知しない.
      }
    },
  };
};
